import type { ApiResponse, RequestOptions } from "@/shared/types/request";
import {
	type AccessPolicy,
	type AccessPolicyProfessional,
	type AccessPolicyScope,
	accessPolicyProfessionalListSchema,
	accessPolicySchema,
	type ReplaceAccessPolicyBody,
} from "./types";

type ApiRequester = (options: RequestOptions) => Promise<ApiResponse>;

function requireSuccessfulData(response: ApiResponse): unknown {
	if (response.status === "ERROR") {
		throw new Error(response.error || "The access-policy request failed.");
	}
	return response.data;
}

export async function listAccessPolicyProfessionals(
	request: ApiRequester,
): Promise<AccessPolicyProfessional[]> {
	const professionals: AccessPolicyProfessional[] = [];
	let startingAfter: string | undefined;

	do {
		const response = await request({
			endpoint: "/v2/access-policies/professionals",
			method: "GET",
			queryParams: {
				limit: 100,
				...(startingAfter ? { startingAfter } : {}),
			},
		});
		const page = accessPolicyProfessionalListSchema.parse(
			requireSuccessfulData(response),
		);
		professionals.push(...page.data);
		if (!page.hasMore) return professionals;

		const nextCursor = page.data.at(-1)?.profId;
		if (!nextCursor || nextCursor === startingAfter) {
			throw new Error("The professional directory returned an invalid cursor.");
		}
		startingAfter = nextCursor;
	} while (startingAfter);

	return professionals;
}

export async function getAccessPolicy(
	request: ApiRequester,
	scope: AccessPolicyScope,
): Promise<AccessPolicy> {
	const response = await request({
		endpoint: "/v2/access-policies",
		method: "GET",
		queryParams: {
			clientCode: scope.clientCode,
			...(scope.matterId ? { matterId: scope.matterId } : {}),
		},
	});
	return accessPolicySchema.parse(requireSuccessfulData(response));
}

export async function replaceAccessPolicy(
	request: ApiRequester,
	body: ReplaceAccessPolicyBody,
): Promise<AccessPolicy> {
	const response = await request({
		endpoint: "/v2/access-policies",
		method: "PATCH",
		body,
	});
	return accessPolicySchema.parse(requireSuccessfulData(response));
}

export async function resetAccessPolicy(
	request: ApiRequester,
	scope: AccessPolicyScope,
): Promise<AccessPolicy> {
	const response = await request({
		endpoint: "/v2/access-policies",
		method: "DELETE",
		queryParams: {
			clientCode: scope.clientCode,
			...(scope.matterId ? { matterId: scope.matterId } : {}),
		},
	});
	return accessPolicySchema.parse(requireSuccessfulData(response));
}
